import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg, t } from '@lingui/core/macro'
import type { GraphNode } from 'features/knowledgeDocumentViewer/graph/graphTypes'
import { z } from 'zod'

import type { NodeTooltip } from './canvasTypes'
import {
  dexpiTextSchema,
  getNodeLabel,
  labelFields,
  nonBlankTextSchema,
} from './dexpiAttributes'
import { buildOffPageConnectorTooltipRows } from './offPageConnectorTooltipRows'

export function humanizeIdentifier(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (letter: string): string => letter.toUpperCase())
}

const attributeValueSchema = z.union([
  nonBlankTextSchema,
  z.number().transform((value) => i18n.number(value)),
  z.boolean().transform((value) => (value ? t`Yes` : t`No`)),
  z
    .object({ Value: z.number(), Unit: z.string() })
    .transform(({ Value, Unit }) => `${i18n.number(Value)} ${Unit}`),
  z
    .object({
      SingleLanguageStrings: z.array(z.object({ Value: z.unknown() })).min(1),
    })
    .transform(({ SingleLanguageStrings }) =>
      SingleLanguageStrings.flatMap(
        ({ Value }) => dexpiTextSchema.parse(Value) ?? []
      ).join('; ')
    )
    .pipe(nonBlankTextSchema),
])

export function formatAttributeValue(value: unknown): string | undefined {
  return attributeValueSchema.safeParse(value).data
}

function pipeContext(node: GraphNode): GraphNode[] {
  /* eslint-disable lingui/no-unlocalized-strings -- Native DEXPI class and relationship identifiers. */
  if (node.dexpi.dexpi_class !== 'Plant.Piping.Pipe') return []
  const segment = node.getUniqueSourceNode('Connections')
  if (segment?.dexpi.dexpi_class !== 'Plant.Piping.PipingNetworkSegment')
    return []
  const system = segment.getUniqueSourceNode('Segments')
  return system?.dexpi.dexpi_class === 'Plant.Piping.PipingNetworkSystem'
    ? [segment, system]
    : [segment]
  /* eslint-enable lingui/no-unlocalized-strings */
}

function nodeClassLabel(node: GraphNode): string {
  const className = node.data.dexpi_class.split('.').at(-1)
  return humanizeIdentifier(className ?? node.data.dexpi_class)
}

export function getNodeDisplayName(node: GraphNode): string {
  const offPageConnectorNumber =
    node.dexpi.dexpi_class === 'Plant.Piping.FlowInPipeOffPageConnector' ||
    node.dexpi.dexpi_class === 'Plant.Piping.FlowOutPipeOffPageConnector'
      ? node.dexpi.attributes.PipeConnectorNumber
      : undefined
  if (offPageConnectorNumber)
    return t`Off-page connector ${offPageConnectorNumber}`
  return (
    [node, ...pipeContext(node)]
      .map((item) => getNodeLabel(item.data.attributes))
      .find((label) => label !== undefined) ?? nodeClassLabel(node)
  )
}

/* eslint-disable lingui/no-unlocalized-strings -- Attribute keys are DEXPI identifiers. */
const hiddenFields = new Set<string>([
  ...labelFields,
  'PipeConnectorNumber',
  'NominalDiameterNumericalValueRepresentation',
])
/* eslint-enable lingui/no-unlocalized-strings */

const fieldLabels: Record<string, MessageDescriptor> = {
  ProcessInstrumentationFunctions: msg`Function`,
  ProcessInstrumentationFunctionCategory: msg`Category`,
  ProcessInstrumentationFunctionModifier: msg`Modifier`,
  DeviceInformation: msg`Device information`,
}

function sameHeading(left: string, right: string): boolean {
  return left.trim().toLocaleLowerCase() === right.trim().toLocaleLowerCase()
}

function buildNodeHeader(
  node: GraphNode
): Pick<NodeTooltip, 'title' | 'subtitle'> {
  const title = getNodeDisplayName(node)
  const classLabel = nodeClassLabel(node)
  return sameHeading(title, classLabel)
    ? { title }
    : { title, subtitle: classLabel }
}

export function buildNodeTooltip(node: GraphNode): NodeTooltip {
  const rows = new Map<string, string>()
  for (const item of [node, ...pipeContext(node)]) {
    for (const [key, value] of Object.entries(item.data.attributes)) {
      if (hiddenFields.has(key)) continue
      const formatted = formatAttributeValue(value)
      if (formatted === undefined) continue
      rows.set(
        fieldLabels[key] ? i18n._(fieldLabels[key]) : humanizeIdentifier(key),
        formatted
      )
    }
  }
  for (const row of buildOffPageConnectorTooltipRows(node))
    rows.set(row.label, row.value)
  return {
    ...buildNodeHeader(node),
    rows: [...rows].map(([label, value]) => ({ label, value })),
  }
}
